import React, { useRef } from 'react';
import 
  ReactSelect, 
  { 
    MultiValue, 
    ActionMeta, 
    StylesConfig, 
    components 
  } from 'react-select';

interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: SelectOption[];
  value: MultiValue<SelectOption>;
  onChange: (value: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => void;
  isSearchable?: boolean;
  type?: 'year' | 'entity' | 'employee' | 'title';
  closeMenuOnSelect?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  isSearchable = true,
  type = 'entity',
  closeMenuOnSelect = true
}) => {
  const valueRef = useRef<MultiValue<SelectOption>>(value);
  valueRef.current = value;

  const isSelectAllSelected = () => valueRef.current.length === options.length;

  const isOptionSelected = (option: SelectOption) =>
    valueRef.current.some(({ value }) => value === option.value);

  const getValue = () => options.filter(o => value.some(v => v.value === o.value));

  const onChangeHandler = (
    newValue: MultiValue<SelectOption>,
    actionMeta: ActionMeta<SelectOption>
  ) => {
    const { option } = actionMeta;

    if (option?.value === '<SELECT_ALL>') {
      if (isSelectAllSelected()) {
        onChange([], actionMeta); // Deselect all
      } else {
        onChange(options, actionMeta); // Select all
      }
    } else {
      onChange(newValue, actionMeta);
    }
  };

  const ValueContainer = (props: any) => {
    const { children, ...rest } = props;
    const selected = props.getValue() as MultiValue<SelectOption>;

    let displayLabel = '';
    if (type === 'year') {
      if (selected.length <= 3) {
        displayLabel = selected.map((s: SelectOption) => s.label).join(', ');
      } else {
        displayLabel = `${selected.length} items selected`;
      }
    } else {
      if (selected.length === 1) {
        displayLabel = selected[0].label;
      } else if (selected.length > 1) {
        displayLabel = `${selected.length} items selected`;
      }
    }

    return (
      <components.ValueContainer {...rest}>
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayLabel}
        </div>
      </components.ValueContainer>
    );
  };

  const MenuList = (props: any) => {
    const handleSelectAll = () => {
      if (isSelectAllSelected()) {
        onChange([], { action: 'deselect-option', option: undefined });
      } else {
        onChange(options, { action: 'select-option', option: undefined });
      }
    };

    const handleDeselectAll = () => onChange([], { action: 'deselect-option', option: undefined });

    const buttonStyle: React.CSSProperties = {
      width: 'calc(50% - 4px)',
      padding: '4px 0',
      margin: '0 2px',
      cursor: 'pointer',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '3px',
      textAlign: 'center',
      transition: 'background-color 0.2s',
    };

    const buttonHoverStyle: React.CSSProperties = {
      backgroundColor: '#f0f0f0',
    };

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px' }}>
          <div
            style={buttonStyle}
            onClick={handleSelectAll}
            role="button"
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor!)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor!)}
          >
            Select All
          </div>
          <div
            style={buttonStyle}
            onClick={handleDeselectAll}
            role="button"
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = buttonHoverStyle.backgroundColor!)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor!)}
          >
            Deselect All
          </div>
        </div>
        <components.MenuList {...props}>{props.children}</components.MenuList>
      </div>
    );
  };

  const customStyles: StylesConfig<SelectOption, true> = {
    multiValue: (base) => ({ ...base, display: 'none' }),
    valueContainer: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
  };

  return (
    <ReactSelect
      instanceId="multi-select-unique-id"
      aria-label={`Select ${type}`}
      isOptionSelected={isOptionSelected}
      options={options}
      value={getValue()}
      onChange={onChangeHandler}
      hideSelectedOptions={false}
      closeMenuOnSelect={closeMenuOnSelect}
      isMulti
      isSearchable={isSearchable}
      styles={customStyles}
      components={{ ValueContainer, MenuList }}
    />
  );
};